import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { containsProfanity } from '../utils/profanityFilter';
import EventAuthBar from '../components/EventAuthBar';
import { signalTransitionReady } from '../components/EventTransition';
import { useAppAuth } from '../context/AuthContext';
import { renderMinecraftTextHTML } from '../utils/minecraftIcons';
import useTurnstileToken from '../utils/useTurnstileToken';
import VerifyingProgress from '../components/VerifyingProgress';
import './ChunkDeletePage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const DISPLAY_NAME_KEY = 'thetopia_event_displayname';

/* ── Fingerprint (lazy-loaded) ── */
let fpPromise = null;
async function getFingerprint() {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then(async (FP) => {
      const agent = await FP.load();
      const result = await agent.get();
      return result.visitorId;
    });
  }
  return fpPromise;
}

/* ── Coordinate helpers ── */
function worldToPixel(worldCoord, borderRadius, canvasSize) {
  return ((worldCoord + borderRadius) / (borderRadius * 2)) * canvasSize;
}

function pixelToWorld(pixel, borderRadius, canvasSize) {
  return (pixel / canvasSize) * (borderRadius * 2) - borderRadius;
}

function pixelToChunk(px, py, canvasSize, borderRadius) {
  const worldX = pixelToWorld(px, borderRadius, canvasSize);
  const worldZ = pixelToWorld(py, borderRadius, canvasSize);
  return { chunkX: Math.floor(worldX / 16), chunkZ: Math.floor(worldZ / 16) };
}

/* ── Head image loader ── */
function loadHead(uuid, cache, onLoad) {
  if (cache.has(uuid)) return;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = `https://crafatar.com/avatars/${uuid}?size=32&overlay`;
  img.onload = () => { cache.set(uuid, img); onLoad?.(); };
  img.onerror = () => { cache.set(uuid, null); };
}

/* ── Heatmap color ── */
function heatColor(ratio) {
  // 0→transparent yellow, 1→opaque red
  const r = 255;
  const g = Math.round(255 * (1 - ratio * 0.85));
  const b = 0;
  const a = 0.15 + ratio * 0.55;
  return `rgba(${r},${g},${b},${a})`;
}

/* ── Username Gate ── */
function UsernameGate({ onSubmit }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 24) {
      setError('Name must be 2-24 characters');
      return;
    }
    if (containsProfanity(trimmed)) {
      setError('Please choose an appropriate name');
      return;
    }
    localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
    onSubmit(trimmed);
  };

  return (
    <div className="cd-gate">
      <div className="cd-gate-card">
        <h2 className="cd-gate-title">Enter Display Name</h2>
        <p className="cd-gate-desc">This is how other viewers will see you</p>
        <form onSubmit={handleSubmit} className="cd-gate-form">
          <input
            type="text"
            className="cd-gate-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder="Your name..."
            maxLength={24}
            autoFocus
          />
          {error && <span className="cd-gate-error">{error}</span>}
          <button type="submit" className="cd-gate-btn">Join Event</button>
        </form>
      </div>
    </div>
  );
}

/* ── Round Header ── */
function RoundHeader({ state }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (state.roundState === 'active' && state.roundStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.roundStartedAt - (state.pausedElapsed || 0);
        setTimeLeft(Math.max(0, Math.ceil(((state.roundDurationSec * 1000) - elapsed) / 1000)));
      };
      tick();
      const iv = setInterval(tick, 250);
      return () => clearInterval(iv);
    }
    if (state.roundState === 'intermission' && state.intermissionStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.intermissionStartedAt - (state.pausedElapsed || 0);
        setTimeLeft(Math.max(0, Math.ceil(((state.intermissionSec * 1000) - elapsed) / 1000)));
      };
      tick();
      const iv = setInterval(tick, 250);
      return () => clearInterval(iv);
    }
    if (state.roundState === 'paused' && state.pausedAt) {
      if (state.prePauseState === 'intermission' && state.intermissionStartedAt) {
        const elapsed = state.pausedAt - state.intermissionStartedAt - (state.pausedElapsed || 0);
        setTimeLeft(Math.max(0, Math.ceil(((state.intermissionSec * 1000) - elapsed) / 1000)));
      } else if (state.roundStartedAt) {
        const elapsed = state.pausedAt - state.roundStartedAt - (state.pausedElapsed || 0);
        setTimeLeft(Math.max(0, Math.ceil(((state.roundDurationSec * 1000) - elapsed) / 1000)));
      }
      return;
    }
    setTimeLeft(null);
  }, [state.roundStartedAt, state.roundDurationSec, state.pausedElapsed, state.roundState,
      state.pausedAt, state.intermissionStartedAt, state.intermissionSec, state.prePauseState]);

  const fmt = (s) => s == null ? '-' : `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const info = {
    active:       { label: 'LIVE',          cls: 'cd-badge-live',          hint: 'Click a chunk on the map to vote for its deletion!' },
    intermission: { label: 'INTERMISSION',  cls: 'cd-badge-intermission',  hint: 'The winning chunk is being erased. Next round starts soon!' },
    paused:       { label: 'PAUSED',        cls: 'cd-badge-paused',        hint: 'The event is paused. Hang tight!' },
    idle:         { label: 'WAITING',       cls: 'cd-badge-idle',          hint: 'The event hasn\'t started yet. Stay on this page!' },
  }[state.roundState] || { label: state.roundState?.toUpperCase(), cls: '', hint: '' };

  return (
    <>
      <div className="cd-round-header">
        <div className="cd-round-info">
          <span className={`cd-state-badge ${info.cls}`}>{info.label}</span>
          {state.roundNumber > 0 && <span className="cd-round-number">Round {state.roundNumber}</span>}
        </div>
        <div className={`cd-timer${state.roundState === 'paused' ? ' cd-timer-paused' : ''}`}>{fmt(timeLeft)}</div>
        <div className="cd-connections">
          <span className="cd-connections-dot" />
          {state.connections} watching
        </div>
      </div>
      {info.hint && <div className={`cd-status-hint cd-status-hint-${state.roundState}`}>{info.hint}</div>}
    </>
  );
}

/* ── World Map Canvas ── */
const WorldMap = React.memo(function WorldMap({ roomState, onVoteChunk }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const playersRef = useRef(new Map());    // uuid → { renderX, renderZ, targetX, targetZ, yaw, name }
  const headCacheRef = useRef(new Map());  // uuid → Image|null
  const hoveredRef = useRef(null);         // { chunkX, chunkZ } | null
  const animRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState(600);

  const borderRadius = roomState?.borderRadius || 500;

  // Responsive sizing
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setCanvasSize(Math.min(Math.floor(w), 800));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Update player targets
  const latestPlayers = useRef(roomState?.players);
  latestPlayers.current = roomState?.players;

  useEffect(() => {
    const incoming = roomState?.players;
    if (!incoming) return;
    const current = playersRef.current;
    const seen = new Set();

    for (const p of incoming) {
      seen.add(p.uuid);
      const existing = current.get(p.uuid);
      if (existing) {
        existing.targetX = p.x;
        existing.targetZ = p.z;
        existing.yaw = p.yaw;
        existing.name = p.name;
      } else {
        current.set(p.uuid, {
          renderX: p.x, renderZ: p.z,
          targetX: p.x, targetZ: p.z,
          yaw: p.yaw, name: p.name,
        });
        loadHead(p.uuid, headCacheRef.current);
      }
    }

    for (const uuid of current.keys()) {
      if (!seen.has(uuid)) current.delete(uuid);
    }
  }, [roomState?.players]);

  // Store latest state for rendering without re-creating the loop
  const stateRef = useRef(roomState);
  stateRef.current = roomState;

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const size = canvasSize;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      renderMap(ctx, size, borderRadius, stateRef.current, playersRef.current, headCacheRef.current, hoveredRef.current);
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [canvasSize, borderRadius]);

  // Mouse / touch handlers
  const getChunkFromEvent = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const scaleX = canvasSize / rect.width;
    const scaleY = canvasSize / rect.height;
    return pixelToChunk(x * scaleX, y * scaleY, canvasSize, borderRadius);
  }, [canvasSize, borderRadius]);

  const handleMouseMove = useCallback((e) => {
    hoveredRef.current = getChunkFromEvent(e);
  }, [getChunkFromEvent]);

  const handleClick = useCallback((e) => {
    const chunk = getChunkFromEvent(e);
    if (chunk) onVoteChunk(chunk.chunkX, chunk.chunkZ);
  }, [getChunkFromEvent, onVoteChunk]);

  const handleTouchEnd = useCallback((e) => {
    if (e.changedTouches.length === 0) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.changedTouches[0].clientX - rect.left;
    const y = e.changedTouches[0].clientY - rect.top;
    const scaleX = canvasSize / rect.width;
    const scaleY = canvasSize / rect.height;
    const chunk = pixelToChunk(x * scaleX, y * scaleY, canvasSize, borderRadius);
    if (chunk) onVoteChunk(chunk.chunkX, chunk.chunkZ);
  }, [canvasSize, borderRadius, onVoteChunk]);

  // Hover info
  const [hoverInfo, setHoverInfo] = useState(null);
  const handleMouseMoveInfo = useCallback((e) => {
    handleMouseMove(e);
    const chunk = getChunkFromEvent(e);
    if (!chunk) return setHoverInfo(null);
    const key = `${chunk.chunkX},${chunk.chunkZ}`;
    const votes = stateRef.current?.chunkVotes?.[key] || 0;
    const deleted = stateRef.current?.deletedChunks?.includes(key);
    setHoverInfo({ chunkX: chunk.chunkX, chunkZ: chunk.chunkZ, votes, deleted });
  }, [handleMouseMove, getChunkFromEvent]);

  return (
    <div ref={containerRef} className="cd-map-container">
      <canvas
        ref={canvasRef}
        className="cd-map-canvas"
        style={{ width: canvasSize, height: canvasSize }}
        onMouseMove={handleMouseMoveInfo}
        onMouseLeave={() => { hoveredRef.current = null; setHoverInfo(null); }}
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
      />
      {hoverInfo && (
        <div className="cd-map-tooltip">
          <span className="cd-map-tooltip-coord">Chunk [{hoverInfo.chunkX}, {hoverInfo.chunkZ}]</span>
          {hoverInfo.deleted
            ? <span className="cd-map-tooltip-deleted">DELETED</span>
            : <span className="cd-map-tooltip-votes">{hoverInfo.votes} vote{hoverInfo.votes !== 1 ? 's' : ''}</span>
          }
        </div>
      )}
    </div>
  );
});

/* ── Map rendering ── */
function renderMap(ctx, size, borderRadius, state, players, headCache, hovered) {
  const borderSize = borderRadius * 2;
  const scale = size / borderSize;
  const chunkPx = 16 * scale;

  // Background
  ctx.fillStyle = '#3d6b24';
  ctx.fillRect(0, 0, size, size);

  // Subtle noise pattern (grid dots)
  ctx.fillStyle = 'rgba(0,0,0,0.04)';
  const step = Math.max(chunkPx, 4);
  for (let x = 0; x < size; x += step * 2) {
    for (let y = 0; y < size; y += step * 2) {
      ctx.fillRect(x, y, step, step);
    }
  }

  // Deleted chunks (void)
  if (state?.deletedChunks) {
    for (const key of state.deletedChunks) {
      const [cx, cz] = key.split(',').map(Number);
      const px = worldToPixel(cx * 16, borderRadius, size);
      const pz = worldToPixel(cz * 16, borderRadius, size);
      // Dark void with subtle gradient
      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(px, pz, chunkPx, chunkPx);
      // Inner glow
      ctx.fillStyle = 'rgba(80, 0, 120, 0.3)';
      ctx.fillRect(px + 1, pz + 1, chunkPx - 2, chunkPx - 2);
    }
  }

  // Chunk grid
  ctx.strokeStyle = 'rgba(0,0,0,0.12)';
  ctx.lineWidth = 0.5;
  const minChunk = Math.floor(-borderRadius / 16);
  const maxChunk = Math.ceil(borderRadius / 16);
  for (let c = minChunk; c <= maxChunk; c++) {
    const p = worldToPixel(c * 16, borderRadius, size);
    ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, size); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(size, p); ctx.stroke();
  }

  // Heatmap overlay
  if (state?.chunkVotes) {
    const vals = Object.values(state.chunkVotes);
    const maxVotes = vals.length > 0 ? Math.max(...vals) : 1;
    for (const [key, votes] of Object.entries(state.chunkVotes)) {
      const [cx, cz] = key.split(',').map(Number);
      const px = worldToPixel(cx * 16, borderRadius, size);
      const pz = worldToPixel(cz * 16, borderRadius, size);
      ctx.fillStyle = heatColor(votes / maxVotes);
      ctx.fillRect(px, pz, chunkPx, chunkPx);
    }
  }

  // Your vote highlight
  if (state?.yourVote) {
    const [cx, cz] = state.yourVote.split(',').map(Number);
    const px = worldToPixel(cx * 16, borderRadius, size);
    const pz = worldToPixel(cz * 16, borderRadius, size);
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2.5;
    ctx.strokeRect(px + 1, pz + 1, chunkPx - 2, chunkPx - 2);
  }

  // Hover highlight
  if (hovered) {
    const px = worldToPixel(hovered.chunkX * 16, borderRadius, size);
    const pz = worldToPixel(hovered.chunkZ * 16, borderRadius, size);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2;
    ctx.strokeRect(px, pz, chunkPx, chunkPx);
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(px, pz, chunkPx, chunkPx);
  }

  // Center crosshair (0,0)
  const center = size / 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(center - 12, center); ctx.lineTo(center + 12, center);
  ctx.moveTo(center, center - 12); ctx.lineTo(center, center + 12);
  ctx.stroke();

  // Players
  const headSize = Math.max(16, Math.min(chunkPx * 0.9, 32));
  for (const [uuid, p] of players) {
    // Interpolate
    p.renderX += (p.targetX - p.renderX) * 0.07;
    p.renderZ += (p.targetZ - p.renderZ) * 0.07;

    const px = worldToPixel(p.renderX, borderRadius, size);
    const pz = worldToPixel(p.renderZ, borderRadius, size);

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.beginPath();
    ctx.arc(px + 1, pz + 1, headSize / 2 + 1, 0, Math.PI * 2);
    ctx.fill();

    // Head image or fallback circle
    const headImg = headCache.get(uuid);
    if (headImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(px, pz, headSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(headImg, px - headSize / 2, pz - headSize / 2, headSize, headSize);
      ctx.restore();
      // Border
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(px, pz, headSize / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.fillStyle = '#ccc';
      ctx.beginPath();
      ctx.arc(px, pz, headSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Name tag
    ctx.font = `bold ${Math.max(10, headSize * 0.55)}px monospace`;
    ctx.textAlign = 'center';
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.lineWidth = 3;
    ctx.strokeText(p.name, px, pz + headSize / 2 + 13);
    ctx.fillStyle = '#fff';
    ctx.fillText(p.name, px, pz + headSize / 2 + 13);
  }

  // Border outline
  ctx.strokeStyle = 'rgba(255,60,60,0.5)';
  ctx.lineWidth = 2;
  ctx.strokeRect(0, 0, size, size);
}

/* ── Round History ── */
const RoundHistory = React.memo(function RoundHistory({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="cd-history">
      <h3 className="cd-history-title">Deleted Chunks</h3>
      <div className="cd-history-list">
        {[...history].reverse().map((entry) => (
          <div key={entry.roundNumber} className="cd-history-item">
            <span className="cd-history-round">R{entry.roundNumber}</span>
            <span className="cd-history-chunk">
              Chunk [{entry.winner.chunkX}, {entry.winner.chunkZ}]
            </span>
            <span className="cd-history-meta">
              {entry.winner.votes} vote{entry.winner.votes !== 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

/* ── Main Page ── */
export default function ChunkDeletePage({ event }) {
  const slug = event.slug;
  const { isSignedIn, minecraftUsername } = useAppAuth();
  const { token: sessionToken, error: turnstileError, retry: retryTurnstile } = useTurnstileToken();

  const [displayName, setDisplayName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || '');

  // Auto-set display name from Minecraft account when signed in
  useEffect(() => {
    if (isSignedIn && minecraftUsername && displayName !== minecraftUsername) {
      localStorage.setItem(DISPLAY_NAME_KEY, minecraftUsername);
      setDisplayName(minecraftUsername);
    }
  }, [isSignedIn, minecraftUsername, displayName]);
  const [fingerprint, setFingerprint] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [wsError, setWsError] = useState(null);
  const [lastError, setLastError] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  // Signal transition ready if showing username gate (no WS to wait for)
  useEffect(() => {
    if (!displayName) signalTransitionReady();
  }, [displayName]);

  // Load fingerprint
  useEffect(() => {
    getFingerprint().then(setFingerprint).catch(() => {
      setFingerprint(`anon-${Math.random().toString(36).slice(2, 10)}`);
    });
  }, []);

  // WebSocket connection to /parties/chunk/<slug>
  const connect = useCallback(() => {
    if (!displayName || !fingerprint || !slug || !sessionToken) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${protocol}://${PARTYKIT_HOST}/parties/chunk/${slug}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsError(null);
      signalTransitionReady();
      ws.send(JSON.stringify({ type: 'join', displayName, fingerprint, token: sessionToken }));
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'state') {
          setRoomState(msg);
        } else if (msg.type === 'update') {
          setRoomState((prev) => {
            if (!prev) return null;
            let chunkVotes;
            if (msg.delta) {
              // New server: merge delta into existing
              chunkVotes = { ...prev.chunkVotes };
              if (msg.chunkVotes) {
                for (const [key, count] of Object.entries(msg.chunkVotes)) {
                  if (count === 0) delete chunkVotes[key];
                  else chunkVotes[key] = count;
                }
              }
            } else {
              // Old server fallback: full replace
              chunkVotes = msg.chunkVotes ?? prev.chunkVotes;
            }
            return {
              ...prev,
              chunkVotes,
              ...(msg.connections !== undefined && { connections: msg.connections }),
              ...(msg.yourVote !== undefined && { yourVote: msg.yourVote }),
            };
          });
        } else if (msg.type === 'vote-ack') {
          setRoomState((prev) => prev ? { ...prev, yourVote: msg.yourVote } : null);
        } else if (msg.type === 'viewers') {
          setRoomState((prev) => prev ? { ...prev, connections: msg.connections } : null);
        } else if (msg.type === 'players') {
          setRoomState((prev) => prev ? { ...prev, players: msg.players } : null);
        } else if (msg.type === 'error') {
          setLastError(msg.message);
          setTimeout(() => setLastError(null), 4000);
        }
      } catch { /* ignore */ }
    };

    ws.onclose = () => {
      wsRef.current = null;
      reconnectRef.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => setWsError('Connection lost. Reconnecting...');
  }, [displayName, fingerprint, slug, sessionToken]);

  useEffect(() => {
    if (displayName && fingerprint && sessionToken) connect();
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect, displayName, fingerprint]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const handleVoteChunk = useCallback((chunkX, chunkZ) => {
    sendMessage({ type: 'vote-chunk', chunkX, chunkZ, fingerprint });
  }, [sendMessage, fingerprint]);

  // Username gate
  if (!displayName) {
    return (
      <section className="cd-page">
        <EventAuthBar />
        <UsernameGate onSubmit={setDisplayName} />
      </section>
    );
  }

  const isActive = roomState?.roundState === 'active';

  return (
    <section className="cd-page">
      <EventAuthBar />
      <div className="cd-container">
        <h1 className="cd-event-title" dangerouslySetInnerHTML={{ __html: renderMinecraftTextHTML(event.name || 'Chunk Delete') }} />

        {wsError && <div className="cd-ws-error">{wsError}</div>}
        {lastError && <div className="cd-cmd-error">{lastError}</div>}

        {roomState ? (
          <>
            <RoundHeader state={roomState} />
            <WorldMap
              roomState={roomState}
              onVoteChunk={handleVoteChunk}
            />
            <RoundHistory history={roomState.history} />
          </>
        ) : (
          <div className="cd-connecting">
            {turnstileError ? (
              <>
                <span>Verification failed</span>
                <button className="le-retry-btn" onClick={retryTurnstile}>Retry</button>
              </>
            ) : (
              <VerifyingProgress
                done={!!sessionToken && !!roomState}
                label={!sessionToken ? 'Verifying...' : 'Connecting to live server...'}
              />
            )}
          </div>
        )}

        <div className="cd-user-bar">
          <span className="cd-user-name">Playing as: {displayName}</span>
          {!isSignedIn && (
            <button
              className="cd-change-name"
              onClick={() => { localStorage.removeItem(DISPLAY_NAME_KEY); setDisplayName(''); }}
            >
              Change Name
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
